/**
 * Logique de l'écran « Comptes de paie » (Comptabilité > Comptes et TVA).
 *
 * Aucun numéro de compte n'est écrit ici : tout vient de `CORRESPONDANCE_COMPTES_PAIE` et de la table
 * de l'entreprise. La validation réutilise `resoudreComptesPaie` (même règle qu'à l'enregistrement) pour que
 * l'écran affiche l'erreur AVANT l'envoi, champ par champ.
 */
import {
  COMPTES_PAIE_PAR_DEFAUT,
  CORRESPONDANCE_COMPTES_PAIE,
  ComptesPaieInvalideError,
  ROLES_COMPTE_PAIE,
  resoudreComptesPaie,
  type EcartReferentielV2,
  type RoleComptePaie,
  type TableComptesPaie,
} from '@domain/comptabilite/ComptesPaie';

export const LIBELLE_ECART_COMPTE_PAIE: Readonly<Record<EcartReferentielV2, string>> = {
  identique: 'Identique',
  sous_compte: 'Sous-compte du même compte',
  different: 'Différent',
  hors_referentiel: 'Hors référentiel',
  a_preciser: 'À préciser',
};

/** Un écart à valider = tout ce qui n'est ni identique ni un simple sous-compte du référentiel v2. */
export function ecartAValider(ecart: EcartReferentielV2): boolean {
  return ecart === 'different' || ecart === 'hors_referentiel' || ecart === 'a_preciser';
}

export interface LigneEcranComptePaie {
  readonly role: RoleComptePaie;
  readonly libelle: string;
  readonly sens: 'Débit' | 'Crédit';
  readonly compteUtilise: string;
  readonly compteDefaut: string;
  readonly compteReferentielV2: string;
  readonly ecart: EcartReferentielV2;
  readonly ecartLibelle: string;
  readonly aValider: boolean;
  readonly modifie: boolean;
  readonly commentaire: string;
  readonly classe: 4 | 6 | 7;
}

export function construireLignesComptesPaie(table: TableComptesPaie): LigneEcranComptePaie[] {
  return CORRESPONDANCE_COMPTES_PAIE.map((l) => ({
    role: l.role,
    libelle: l.libelle,
    sens: l.sens === 'debit' ? 'Débit' : 'Crédit',
    compteUtilise: table[l.role],
    compteDefaut: l.compteDefaut,
    compteReferentielV2: l.compteReferentielV2,
    ecart: l.ecart,
    ecartLibelle: LIBELLE_ECART_COMPTE_PAIE[l.ecart],
    aValider: ecartAValider(l.ecart),
    modifie: table[l.role] !== l.compteDefaut,
    commentaire: l.commentaire,
    classe: l.classe,
  }));
}

export type SaisieComptesPaie = Record<RoleComptePaie, string>;

export function saisieDepuisTable(table: TableComptesPaie): SaisieComptesPaie {
  const out = {} as SaisieComptesPaie;
  for (const role of ROLES_COMPTE_PAIE) out[role] = table[role];
  return out;
}

export type ResultatValidationComptesPaie =
  | { ok: true; table: TableComptesPaie }
  | { ok: false; erreurs: Partial<Record<RoleComptePaie, string>>; resume: string };

const FORMAT_COMPTE = /^[1-9]\d{2,7}$/;

/**
 * Valide la saisie complète. Les erreurs de format et de classe sont rattachées à leur ligne ;
 * l'ambiguïté (même compte pour deux familles) est rattachée à toutes les lignes concernées.
 */
export function validerSaisieComptesPaie(saisie: SaisieComptesPaie): ResultatValidationComptesPaie {
  const erreurs: Partial<Record<RoleComptePaie, string>> = {};
  const propres: Partial<Record<RoleComptePaie, string>> = {};
  for (const l of CORRESPONDANCE_COMPTES_PAIE) {
    const brut = String(saisie[l.role] ?? '').trim();
    if (brut === '') {
      erreurs[l.role] = 'Compte obligatoire : saisissez 3 à 8 chiffres.';
      continue;
    }
    if (!FORMAT_COMPTE.test(brut)) {
      erreurs[l.role] = `« ${brut} » n'est pas un compte valable : 3 à 8 chiffres attendus, sans lettre ni espace.`;
      continue;
    }
    if (Number(brut[0]) !== l.classe) {
      erreurs[l.role] = `Le compte ${brut} ne convient pas ici : il faut un compte de classe ${l.classe} (commençant par ${l.classe}).`;
      continue;
    }
    propres[l.role] = brut;
  }
  if (Object.keys(erreurs).length > 0) return refus(erreurs);

  try {
    const table = resoudreComptesPaie(propres);
    return { ok: true, table };
  } catch (err) {
    if (!(err instanceof ComptesPaieInvalideError)) throw err;
    const compte = /Compte (\d+) utilisé/.exec(err.message)?.[1];
    if (compte) {
      for (const l of CORRESPONDANCE_COMPTES_PAIE) {
        if (propres[l.role] === compte) {
          erreurs[l.role] =
            `Le compte ${compte} sert déjà à une autre famille de comptes (lecture ambiguë dans le référentiel). Choisissez un compte différent pour « ${l.libelle} ».`;
        }
      }
    }
    if (Object.keys(erreurs).length === 0) {
      const premier = ROLES_COMPTE_PAIE[0]!;
      erreurs[premier] = err.message;
    }
    return refus(erreurs);
  }
}

function refus(erreurs: Partial<Record<RoleComptePaie, string>>): ResultatValidationComptesPaie {
  const n = Object.keys(erreurs).length;
  return {
    ok: false,
    erreurs,
    resume: n === 1 ? '1 compte à corriger avant d’enregistrer.' : `${n} comptes à corriger avant d’enregistrer.`,
  };
}

/** Compte par défaut d'un rôle (bouton « Rétablir ce compte »). */
export function compteParDefaut(role: RoleComptePaie): string {
  return COMPTES_PAIE_PAR_DEFAUT[role];
}
